import React from 'react';
import { Link } from 'react-router-dom';
import { Users, UserCheck, Handshake, IndianRupee, Rocket, Brain, Award } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const HowWeHelpPage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="how-we-help-page">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden bg-gradient-to-br from-cream via-pragmatic/10 to-richolive/5">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pragmatic/15 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-richolive/10 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
                Our Support System
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-deepwalnut mb-6">
                How We Support <span className="text-richolive">Every Participant</span>
              </h1>
              <p className="text-xl text-heavyrain leading-relaxed mb-8">
                At SET, participation is not just about attending a competition. It is a complete journey of preparation, growth, and confidence building.
              </p>
              <p className="text-lg text-deepwalnut leading-relaxed max-w-3xl mx-auto">
                From the time a team is selected, we create a strong support system around them. Our aim is simple. Help every student feel prepared, supported, and ready to represent India on the global stage.
              </p>
            </div>
          </div>
        </section>

        {/* Mentorship Section */}
        <section className="py-20 bg-cream-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pragmatic/30 to-richolive/20 flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-dwarfpony" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-deepwalnut mb-6">
                  Mentorship from STEM Experts
                </h2>
                <div className="space-y-4 text-heavyrain text-lg leading-relaxed">
                  <p>
                    Our teams work with mentors from different STEM fields. These include professors, industry professionals, researchers, and entrepreneurs.
                  </p>
                  <p>
                    Mentors guide students in understanding the problem clearly and building strong solutions. They also help improve communication skills, presentation style, and overall thinking approach.
                  </p>
                  <p className="font-semibold text-richolive">
                    The focus is not just on finishing a project. The focus is on learning deeply and growing through the process.
                  </p>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl border border-pragmatic/30 bg-white">
                  {/* Placeholder for mentorship image */}
                  <div className="absolute inset-0 bg-gradient-to-br from-pragmatic/20 to-richolive/10 flex items-center justify-center">
                    <Users className="w-24 h-24 text-pragmatic/40" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dedicated Trainer Section */}
        <section className="py-20 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl border border-pragmatic/30 bg-white">
                  {/* Placeholder for trainer image */}
                  <div className="absolute inset-0 bg-gradient-to-br from-richolive/10 to-pragmatic/20 flex items-center justify-center">
                    <UserCheck className="w-24 h-24 text-richolive/40" />
                  </div>
                </div>
              </div>
              <div>
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-richolive/20 to-pragmatic/10 flex items-center justify-center mb-6">
                  <UserCheck className="w-8 h-8 text-richolive" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-deepwalnut mb-6">
                  Dedicated One-to-One Trainer
                </h2>
                <div className="space-y-4 text-heavyrain text-lg leading-relaxed">
                  <p>
                    Each team is assigned a trainer who works closely with them throughout the preparation period.
                  </p>
                  <p>
                    The trainer helps break down the problem statement, set weekly goals, review progress, and conduct mock practice sessions.
                  </p>
                  <p className="font-semibold text-dwarfpony">
                    This ensures that every team gets personal attention and steady guidance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Founders Interaction Section */}
        <section className="py-20 bg-cream-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-dwarfpony/20 to-richolive/10 flex items-center justify-center mb-6">
                  <Handshake className="w-8 h-8 text-dwarfpony" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-deepwalnut mb-6">
                  Interaction with Founders and Leaders
                </h2>
                <div className="space-y-4 text-heavyrain text-lg leading-relaxed">
                  <p>
                    Students also get the chance to meet startup founders, corporate leaders, and investors.
                  </p>
                  <p>
                    These sessions help students understand how real world problem solving works. They learn how ideas turn into companies and how innovation creates impact.
                  </p>
                  <p className="font-semibold text-richolive">
                    It broadens their thinking and builds confidence.
                  </p>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl border border-pragmatic/30 bg-white">
                  {/* Placeholder for founders image */}
                  <div className="absolute inset-0 bg-gradient-to-br from-dwarfpony/10 to-richolive/20 flex items-center justify-center">
                    <Handshake className="w-24 h-24 text-dwarfpony/40" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sponsorship Section */}
        <section className="py-20 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-pragmatic/10 to-richolive/5 border border-dwarfpony/20 rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto shadow-lg">
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mx-auto mb-8 shadow-md">
                <IndianRupee className="w-10 h-10 text-richolive" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-deepwalnut mb-6">
                Sponsorship and Financial Support
              </h2>
              <p className="text-lg text-heavyrain leading-relaxed mb-6">
                We believe that financial limitations should not stop talent. SET helps teams raise sponsorship support to cover participation fees, travel, logistics, and equipment costs.
              </p>
              <p className="text-xl font-semibold text-dwarfpony">
                Our goal is to make sure deserving students get the opportunity they earn.
              </p>
            </div>
          </div>
        </section>

        {/* Growth Section */}
        <section className="py-20 bg-cream-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
                Real Impact
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-deepwalnut mb-6">
                Personality Development and <span className="text-richolive">Real Growth</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-cream border border-pragmatic/30 rounded-xl p-8 hover:shadow-lg transition-all text-center group hover:border-dwarfpony">
                <div className="w-14 h-14 rounded-full bg-pragmatic/20 flex items-center justify-center mx-auto mb-6 group-hover:bg-dwarfpony/20 transition-colors">
                  <Brain className="w-7 h-7 text-dwarfpony" />
                </div>
                <h3 className="text-xl font-bold text-deepwalnut mb-4">Personal Growth</h3>
                <p className="text-heavyrain">
                  The biggest impact is not just technical. It is personal growth. Students report strong improvement in teamwork, creativity, leadership, time management, and handling unexpected challenges.
                </p>
              </div>

              <div className="bg-cream border border-pragmatic/30 rounded-xl p-8 hover:shadow-lg transition-all text-center group hover:border-richolive">
                <div className="w-14 h-14 rounded-full bg-richolive/20 flex items-center justify-center mx-auto mb-6 group-hover:bg-richolive/30 transition-colors">
                  <Rocket className="w-7 h-7 text-richolive" />
                </div>
                <h3 className="text-xl font-bold text-deepwalnut mb-4">Practical Application</h3>
                <p className="text-heavyrain">
                  They learn to apply math and science in practical ways. They learn to think clearly under pressure. Most importantly, they leave more confident than when they started.
                </p>
              </div>

              <div className="bg-cream border border-pragmatic/30 rounded-xl p-8 hover:shadow-lg transition-all text-center group hover:border-dwarfpony">
                <div className="w-14 h-14 rounded-full bg-dwarfpony/20 flex items-center justify-center mx-auto mb-6 group-hover:bg-dwarfpony/30 transition-colors">
                  <Award className="w-7 h-7 text-dwarfpony-dark" />
                </div>
                <h3 className="text-xl font-bold text-deepwalnut mb-4">Confident Leaders</h3>
                <p className="text-heavyrain">
                  We are helping them become problem solvers, team players, and confident young leaders who can represent India proudly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion / CTA */}
        <section className="py-20 bg-gradient-to-br from-midnight to-richolive-dark text-white text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              The Bigger Outcome
            </h2>
            <p className="text-xl text-pragmatic mb-8 leading-relaxed">
              We are not just preparing students for a competition. We are helping them become problem solvers, team players, and confident young leaders who can represent India proudly.
            </p>
            <p className="text-2xl font-bold mb-10">
              Every team finishes the journey stronger than they began.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-dwarfpony/25 w-full sm:w-auto">
                  Apply Now
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="border-pragmatic text-pragmatic hover:bg-pragmatic/10 hover:text-white px-8 py-6 text-lg w-full sm:w-auto bg-transparent">
                  Learn About SET
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowWeHelpPage;
